import { useCallback, useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useReferenceState } from "lib/@hooks/use-reference"
import { getAvailableApplications } from "plugins/application-switcher/controller/application-switcher.api"
import { getCurrentClientId } from "minimals-template/components/contexts/NognitoContext"
import { emptyArray } from "lib/empty-array"

export function useApplicationSwitcherSync() {
    const location = useLocation()
    const navigate = useNavigate()
    const options = getAvailableApplications.useResults() || emptyArray

    const storageKey = `${getCurrentClientId()}-application-selected`
    const [selectedId, setSelectedId] = useReferenceState(storageKey)

    const didBoot = useRef(false)
    const selectedHydrated = typeof selectedId !== "undefined"

    useEffect(() => {
        if (didBoot.current) return
        if (!options.length) return

        const current = normalisePathname(location.pathname)
        const fromUrl = pickAppByPath(options, current)

        if (fromUrl) {
            if (selectedId !== fromUrl.id) setSelectedId(fromUrl.id)
            didBoot.current = true
            return
        }

        let target = null
        if (selectedHydrated && selectedId) {
            target = options.find((o) => o.id === selectedId) || options[0]
        } else {
            target = options[0]
        }

        if (target?.path) {
            const allowed = getAllowedPrefixes(target)
            const inApp = allowed.some((p) => current === p || current.startsWith(p + "/"))
            if (!inApp) navigate(target.path, { replace: true })
            if (selectedId !== target.id) setSelectedId(target.id || null)
        }

        didBoot.current = true
    }, [options, location.pathname, selectedId, selectedHydrated, navigate, setSelectedId])

    const selectApp = useCallback(
        (id) => {
            setSelectedId(id)
            const found = options.find((o) => o.id === id)
            if (found?.path) navigate(found.path)
        },
        [options, navigate, setSelectedId]
    )

    const selected = options.find((o) => o.id === selectedId) || options[0] || null

    const shouldShow = (options?.length ?? 0) > 1
    return { options, selected, selectedId, selectApp, shouldShow }
}

function normalisePathname(p = "") {
    if (!p) return "/"
    return p.endsWith("/") && p !== "/" ? p.slice(0, -1) : p
}

function getAllowedPrefixes(app) {
    return app?.path ? [normalisePathname(app.path)] : []
}

function pickAppByPath(options, currentPath) {
    const current = normalisePathname(currentPath)
    let winner = null
    let bestLen = -1
    for (const app of options) {
        for (const prefix of getAllowedPrefixes(app)) {
            if (current === prefix || current.startsWith(prefix + "/")) {
                if (prefix.length > bestLen) {
                    bestLen = prefix.length
                    winner = app
                }
            }
        }
    }
    return winner
}
